import { useCallback } from "react";
import { useToast } from "../../../../../components/toast";
import { createDocumentByTenant } from "../../../../../services/document";
import { listDrives } from "../../../../../services/drives";
import { extractDocumentDescription } from "../../utils/contentUtils";

const LIST_DRIVES_PARAMS = {
  scope: "MANAGE",
  meta: {
    page: 1,
    page_size: 10,
    order_by: "created_at",
    order_direction: "DESC",
    filters: {},
  },
};

function parseDrivesResponse(data) {
  if (Array.isArray(data?.body)) return data.body;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.body?.data)) return data.body.data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

function getFirstVaultUuid(items) {
  const first = items[0];
  return first?.vaultUuid ?? first?.vault_uuid ?? first?.id ?? first?.uuid ?? null;
}

function getErrorMessage(err) {
  return (
    err?.response?.data?.msgCode?.[0]?.description ||
    err?.message ||
    "Failed to create document."
  );
}

/** Nombre único para el documento: "Document" + fecha y hora (evita duplicados). Exportado para usar en el top bar del canvas. */
export function getUniqueDocumentName(baseName = "Document") {
  const now = new Date();
  const date = now.toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric" });
  const time = now.toLocaleTimeString("es-CL", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  return baseName === "Document" ? `Document ${date} ${time}` : baseName;
}

/**
 * Hook para crear un documento en el backend a partir del contenido de un mensaje de chat.
 * Usa el primer vault de listDrives (My Workspace) como destino. Solo muestra toast en error.
 * Bajo acoplamiento: no conoce el panel ni los documentos en memoria.
 *
 * @returns {{ createDocumentFromMessage: (params: { title: string, content: string }) => Promise<void> }}
 */
export function useCreateDocumentFromMessage() {
  const toast = useToast();

  const createDocumentFromMessage = useCallback(
    async ({ title, content }) => {
      const docTitle = title ?? "Document";
      const docContent = content ?? "";
      const description = extractDocumentDescription(docContent);

      try {
        const drivesResp = await listDrives(LIST_DRIVES_PARAMS);
        const items = parseDrivesResponse(drivesResp?.data ?? {});
        const vaultUuid = getFirstVaultUuid(items);

        if (!vaultUuid) {
          toast.error({ message: "No workspace available to create the document." });
          return;
        }

        const createBody = {
          name: getUniqueDocumentName(docTitle),
          description,
          type: "text/plain",
          url: "",
          vaultUuid,
          folderUuid: null,
          metadata: {
            source: "chat_message",
            version: "1.0",
            content: docContent,
          },
        };

        const response = await createDocumentByTenant(createBody);
        const data = response?.data || {};
        const statusCode = data.status_code ?? data.statusCode;

        if (statusCode != null && (statusCode < 200 || statusCode >= 300)) {
          const msg =
            data.msgCode?.[0]?.description || "Failed to create document.";
          toast.error({ message: msg });
          return;
        }
        const body = data.body || data;
        const documentUuid =
          body?.document?.document_uuid ?? body?.document?.documentUuid ?? body?.document_uuid;
        return documentUuid ?? undefined;
      } catch (err) {
        toast.error({ message: getErrorMessage(err) });
      }
    },
    [toast]
  );

  return { createDocumentFromMessage };
}
